import {inlineError, tracer} from '@s-ui/decorators'
import {UseCase} from '@s-ui/domain'

/**
 * @implements UseCase
 */
export class GetSubscriptionCarouselAdsUseCase extends UseCase {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Mapper} deps._fromSubscriptionCarouselSearchToSearchRequestVOMapper
   * @param {Function<getSubscriptionSearchResultsAdsErrorFactory>} deps.getSubscriptionSearchResultsAdsErrorFactory
   * @param {Function<getSubscriptionSearchResultsInvalidRequestAdsErrorFactory>} deps.getSubscriptionSearchResultsInvalidRequestAdsErrorFactory
   * @param {Function<searchRequestValueObjectFactory>} deps.searchRequestValueObjectFactory
   * @param {Service} deps.getSubscriptionSearchResultsAdsService
   */
  constructor({
    config,
    fromAdsListValueObjectToSubscriptionCarouselAdsListValueObjectMapper,
    fromSubscriptionCarouselSearchRequestVOToSearchRequestVOMapper,
    fromSubscriptionCarouselSearchRequestVOToSubscriptionSearchRequestVOMapper,
    getSubscriptionSearchResultsAdsErrorFactory,
    getSubscriptionSearchResultsAdsService,
    searchRequestValueObjectFactory
  }) {
    super({config})
    this._config = config
    this._fromAdsListValueObjectToSubscriptionCarouselAdsListValueObjectMapper =
      fromAdsListValueObjectToSubscriptionCarouselAdsListValueObjectMapper
    this._fromSubscriptionCarouselSearchRequestVOToSearchRequestVOMapper =
      fromSubscriptionCarouselSearchRequestVOToSearchRequestVOMapper
    this._fromSubscriptionCarouselSearchRequestVOToSubscriptionSearchRequestVOMapper =
      fromSubscriptionCarouselSearchRequestVOToSubscriptionSearchRequestVOMapper
    this._getSubscriptionSearchResultsAdsErrorFactory = getSubscriptionSearchResultsAdsErrorFactory
    this._getSubscriptionSearchResultsAdsService = getSubscriptionSearchResultsAdsService
    this._searchRequestValueObjectFactory = searchRequestValueObjectFactory
    this._SUBSCRIPTION_CAROUSEL_MIN_ADS = 3
  }

  @tracer({metric: 'GetSubscriptionCarouselAdsUseCase#execute'})
  @inlineError
  async execute(search) {
    const searchRequestValueObject = this._searchRequestValueObjectFactory(search)

    const searchRequestVO =
      this._fromSubscriptionCarouselSearchRequestVOToSearchRequestVOMapper.map(searchRequestValueObject)

    const subscriptionSearchRequestVO =
      this._fromSubscriptionCarouselSearchRequestVOToSubscriptionSearchRequestVOMapper.map(searchRequestVO)

    const [getSubscriptionSearchResultsServiceError, getSubscriptionSearchResultsServiceResponse] =
      await this._getSubscriptionSearchResultsAdsService.execute({
        searchRequestValueObject: searchRequestVO,
        subscriptionSearchRequestVO,
        withAggregations: false
      })

    if (getSubscriptionSearchResultsServiceError)
      Promise.reject(new this._getSubscriptionSearchResultsAdsErrorFactory())

    const hasMakeFilter = Boolean(searchRequestVO.getMakeId())
    const mappedResponse = this._fromAdsListValueObjectToSubscriptionCarouselAdsListValueObjectMapper
      .setParams({hasMakeFilter})
      .map(getSubscriptionSearchResultsServiceResponse)

    const responseItems = mappedResponse.getItems()

    // last minute logic to be removed on experiment roll-out
    if (responseItems.length < this._SUBSCRIPTION_CAROUSEL_MIN_ADS) {
      const searchRequestWithoutMakesVO = this._fromSubscriptionCarouselSearchRequestVOToSearchRequestVOMapper
        .setParams({removeMake: true})
        .map(searchRequestValueObject)

      const subscriptionSearchRequestWithoutMakeVO =
        this._fromSubscriptionCarouselSearchRequestVOToSubscriptionSearchRequestVOMapper
          .setParams({removeMake: true})
          .map(searchRequestWithoutMakesVO)

      const [
        getSubscriptionSearchWithoutMakesResultsServiceError,
        getSubscriptionSearchWithoutMakesResultsServiceResponse
      ] = await this._getSubscriptionSearchResultsAdsService.execute({
        searchRequestValueObject: searchRequestWithoutMakesVO,
        subscriptionSearchRequestWithoutMakeVO
      })

      if (getSubscriptionSearchWithoutMakesResultsServiceError)
        Promise.reject(new this._getSubscriptionSearchResultsAdsErrorFactory())

      const mappedResponseWithoutMake = this._fromAdsListValueObjectToSubscriptionCarouselAdsListValueObjectMapper.map(
        getSubscriptionSearchWithoutMakesResultsServiceResponse
      )

      return {...mappedResponseWithoutMake.toJSON(), hasMakeFilter: false}
    }

    return {...mappedResponse.toJSON(), hasMakeFilter}
  }
}
